<template>
  <a-config-provider :locale="arcoLocale">
    <router-view />
    <race-answer-notify-modal />
    <sign-in-notify-modal />
    <div v-if="linkForYou.show" class="fixed top-0 left-0 w-screen h-screen bg-[rgba(0,0,0,0.5)] flex flex-col flex-center z-999" @click="linkForYou.show = false">
      <div class="w-[clamp(320px,50%,768px)] bg-white py-20px rounded-8px" @click.stop="">
        <div class="text-center mb-16px">
          <div class="font-bold text-16px">
            你的专属链接
          </div>
        </div>
        <div class="px-20px text-12px break-all mb-16px text-12px">
          {{ linkForYou.link }}
        </div>
        <div class="flex flex-center">
          <a-button class="px-60px rounded-full" type="primary" @click="copyLink">
            复制
          </a-button>
        </div>
        <div class="text-12px text-red px-20px">
          请勿分享给陌生人
        </div>
      </div>
    </div>
  </a-config-provider>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { Message } from '@arco-design/web-vue'
import { useEventListener } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import enUS from '@arco-design/web-vue/es/locale/lang/en-us'
import zhCN from '@arco-design/web-vue/es/locale/lang/zh-cn'
import VConsole from 'vconsole'
import { throttle } from 'lodash-es'
import { useSysStore, useUserStore, usePersistStore } from './store'
import { systemApi } from '@/api'
import { subscribeGlobalWebSocketMessage } from '@/api/modules/globalWebsocket'
import { useSignInNotifyModal } from '@/hooks/useSignInNotifyModal'
import SignInNotifyModal from '@/components/sign-in-notify-modal/index.vue'
import { useRaceAnswerNotifyModal } from '@/hooks/useRaceAnswerNotifyModal'
import RaceAnswerNotifyModal from '@/components/race-answer-notify-modal/index.vue'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'

const router = useRouter()
const persistStore = usePersistStore()
const sysStore = useSysStore()

function handleResize() {
  sysStore.setIsSmallScreen(window.screen.width < 768)
}

function isInMp() {
  // @ts-ignore
  return window.__wxjs_environment === 'miniprogram'
}

onMounted(() => {
  persistStore.initDeviceSymbol()
  handleResize()
  sysStore.setIsInMp(isInMp())
  if(import.meta.env.MODE !== 'production' && window.screen.width < 768) {
    new VConsole()
  }
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
const { locale } = useI18n()

watch(locale, val => {
  dayjs.locale(val === 'zh-CN' ? 'zh-cn' : 'en')
}, { immediate: true })

// 动态设置 Arco Design 的语言
const arcoLocale = computed(() => {
  return locale.value === 'en-US' ? enUS : zhCN
})

useEventListener('unhandledrejection',  event => {
  event.promise.catch(e => {
    if(e._toast) {
      Message.error(e._toast)
    } else {
      console.error('Unhandled Promise rejection:',event.reason)
    }
  })
})

const userStore = useUserStore()
const { fetchAndOpenSignInNotifyModal, handleGlobalSignInNotification, signInNotifyState } = useSignInNotifyModal()
const { handleRaceAnswerNotification, fetchAndOpenRaceAnswerNotifyModal } = useRaceAnswerNotifyModal()
let unsubscribeSignInNotify: (() => void) | null = null

const linkForYou = ref({
  link: '',
  show: false
})

function buildLink() {
  const url = new URL(window.location.href)
  if (userStore.token) {
    url.searchParams.set('token', userStore.token)
  }
  return url.toString()
}

function copyLink() {
  if (!linkForYou.value.link) {
    linkForYou.value.link = buildLink()
  }
  navigator.clipboard.writeText(linkForYou.value.link).then(() => {
    Message.success('复制成功')
    linkForYou.value.show = false
  }).catch(() => {
    try {
      const input = document.createElement('input')
      input.value = linkForYou.value.link
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      Message.success('复制成功')
      linkForYou.value.show = false
    } catch (e) {
      Message.error('复制失败')
      linkForYou.value.show = false
    }
  })
}

onMounted(() => {
  function listenKeyDown(e: KeyboardEvent)  {
    const key = typeof e.key === 'string' ? e.key.toLowerCase() : ''
    if (e.ctrlKey && key === 'l') {
      e.preventDefault()
      linkForYou.value.link = buildLink()
      linkForYou.value.show = true
    }
  }
  window.addEventListener('keydown', listenKeyDown)
  onUnmounted(() => {
    window.removeEventListener('keydown', listenKeyDown)
  })
})

onMounted(() => {
  function setBottomZero() {
    if (!window.visualViewport) {
      document.documentElement.style.setProperty('--bottom-zero', '0px')
      return
    }
    const vh = window.innerHeight
    const vvHeight = window.visualViewport.height
    // keyboard 弹起时 vvHeight < vh
    const keyboardHeight = vvHeight - vh
    document.documentElement.style.setProperty('--bottom-zero', `${keyboardHeight}px`)
  }

  setBottomZero()
  window.visualViewport?.addEventListener('resize', setBottomZero)
  window.visualViewport?.addEventListener('scroll', setBottomZero)
})

// 全局点击事件上报（节流：每5秒上报一次）
const throttledReportClick = throttle((event: Event) => {
  const sysStore = useSysStore()
  let device: 'MP' | 'M' | 'PC'
  if (sysStore.isInMp) {
    device = 'MP' // 小程序
  } else if (sysStore.isSmallScreen) {
    device = 'M' // 移动端
  } else {
    device = 'PC' // PC端
  }

  // 构建上报数据
  const reportData = {
    device,
    route: window.location.pathname,
  }
  if(!userStore.token) {
    return
  }
  systemApi.fetchReportClick(reportData)

}, 5000, { leading: true, trailing: false }) // 5秒节流 避免 trailing 重复触发

// 全局点击事件监听
onMounted(() => {
  window.addEventListener('click', throttledReportClick, true)
  window.addEventListener('touchend', throttledReportClick, true)
})

onUnmounted(() => {
  window.removeEventListener('click', throttledReportClick, true)
  window.removeEventListener('touchend', throttledReportClick, true)
})

onMounted(async() => {
  if (userStore.token && router.currentRoute.value.path !== '/') {
    fetchAndOpenSignInNotifyModal()
    // 签到弹窗优先级高于抢答弹窗，签到可见时不弹抢答
    fetchAndOpenRaceAnswerNotifyModal()
  }
  unsubscribeSignInNotify = subscribeGlobalWebSocketMessage(message => {
    handleGlobalSignInNotification(message)
    // 签到弹窗优先级高于抢答/抽选弹窗
    if (!signInNotifyState.visible) {
      handleRaceAnswerNotification(message)
    }
  })
})

onUnmounted(() => {
  if (unsubscribeSignInNotify) {
    unsubscribeSignInNotify()
    unsubscribeSignInNotify = null
  }
})
</script>
